import { warningNotification } from "@app/common/components/notifications";
import SignatureComponent from "@app/common/components/SignatureComponent";
import { Template } from "@app/common/components/Template";
import { formattedDataSelect, isAlphanumeric } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import { Carousel } from "@mantine/carousel";
import "@mantine/carousel/styles.css";

import { useGetExternalClientList } from "@app/services/common/hooks";
import { ExternalClientList } from "@app/services/common/types";
import {
  useGetDocumentList,
  useGetOrderDetail,
  useUpdateMedicineLot
} from "@app/services/drugDelivery/hooks";
import { useGetSupportParamsList } from "@app/services/supportParams/hooks";
import {
  FileAdditionalSupports,
  LotsScannerResponse,
  Medicine,
  OrderListResponse
} from "@app/services/drugDelivery/types";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { calculateAge } from "@app/utils/generateId";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Select,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCamera,
  IconCheck,
  IconEdit,
  IconEye,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";
import classes from "./CarouselStyles.module.css";
import FullDelivery from "./components/CompleteDeliveryRequestForm";
import DeliveryRegisterMultiPicture from "./components/DeliveryRegisterMultiPicture";
import DeliveryRegisterPicture from "./components/DeliveryRegisterPicture";
import DeliverySupportModal from "./components/DeliverySupportModal";
import MedicationScanner from "./components/MedicationScanner";
import DeliveryFormToSend from "./DeliveryFormToSend";
import CreatePatientModal from "./components/CreatePatientModal";
import "./styles.scss";

interface IEditLot {
  medicineId: number;
  lot: string;
  originalLot: string;
}

const alphanumericRegex = /^[a-zA-Z0-9]*$/;

const NIT_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

function calculateVerificationDigit(nit: string): string {
  const digits = nit.replace(/\D/g, "");
  if (!digits.length) {
    return "";
  }
  const reversed = digits.split("").reverse();
  let sum = 0;
  for (let i = 0; i < reversed.length && i < NIT_WEIGHTS.length; i++) {
    sum += Number(reversed[i]) * NIT_WEIGHTS[i];
  }
  const remainder = sum % 11;
  if (remainder >= 2) {
    return String(11 - remainder);
  }
  return String(remainder);
}

const medicalOrderTypeLabels: Record<string, string> = {
  insurance: "Aseguradora",
  particular: "Particular",
  sugess: "Sugerencia"
};

const Delivery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    disId,
    orderId,
    withoutOrder,
    action,
    deliveryBy,
    isPendingCompleteDelivery,
    fullDeliveryIsApproved,
    medicalOrderType: storedMedicalOrderType
  } = useDeliveryData((store) => store.dataDispense);

  const { dataPatient } = useDeliveryData((store) => store.dataToSearch);
  const setDataDispense = useDeliveryData((store) => store.setDataDispense);
  const { can } = usePermissions();
  const canCreate = can(RBAC_ACTIONS.CREATE, MODULE_PERMISSION_PREFIX.MEDICINE_DELIVERY);
  const canDelete = can(RBAC_ACTIONS.DELETE, MODULE_PERMISSION_PREFIX.MEDICINE_DELIVERY);
  const disabledForm = Boolean(action === "detail");
  const showDetail = Boolean(action === "detail");

  const resolvedOrderId = Number(orderId ?? location.state?.orderId) || undefined;
  const resolvedDisId = Number(disId ?? location.state?.disId) || undefined;

  const detailWithoutOrder = Boolean(withoutOrder ?? (!resolvedOrderId && resolvedDisId));

  const isFreeSale = Boolean(deliveryBy === "freeSale");
  const isFreeSaleWithOrder = Boolean(deliveryBy === "freeSaleWithOrder");

  const [freeSaleDocType, setFreeSaleDocType] = useState<string | null>(null);
  const [freeSaleDocNumber, setFreeSaleDocNumber] = useState("");
  const [freeSaleSubmittedDoc, setFreeSaleSubmittedDoc] = useState("");
  const [createPatientModal, setCreatePatientModal] = useState(false);
  const [selectedFreeSaleClient, setSelectedFreeSaleClient] = useState<ExternalClientList | null>(
    null
  );

  const {
    data: documentListRes = {
      results: [],
      rowTotal: 0
    }
  } = useGetDocumentList();

  const freeSaleSearchDoc = useMemo(() => {
    if (!freeSaleSubmittedDoc) {
      return "";
    }
    if (!freeSaleDocType || !documentListRes?.results) {
      return freeSaleSubmittedDoc;
    }
    const found = documentListRes.results.find((dt) => dt.id === Number(freeSaleDocType));
    if (found?.description === "NIT") {
      const dv = calculateVerificationDigit(freeSaleSubmittedDoc);
      return `${freeSaleSubmittedDoc}-${dv}`;
    }
    return freeSaleSubmittedDoc;
  }, [freeSaleSubmittedDoc, freeSaleDocType, documentListRes?.results]);

  const {
    data: freeSalePatientRes,
    isFetching: freeSaleSearching,
    refetch: refetchFreeSale
  } = useGetExternalClientList(
    {
      perpage: 100,
      page: 1,
      documentNumber: freeSaleSearchDoc,
      documentType: freeSaleDocType ?? undefined
    },
    isFreeSale && freeSaleSearchDoc !== "" && freeSaleDocType !== null
  );

  const exactPatientResults = (freeSalePatientRes?.results ?? []).filter(
    (p) => p.documentNumber === freeSaleSearchDoc && p.documentType.id === Number(freeSaleDocType)
  );

  useEffect(() => {
    if (exactPatientResults.length === 1) {
      setSelectedFreeSaleClient(exactPatientResults[0]);
    } else {
      setSelectedFreeSaleClient(null);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeSalePatientRes?.results, freeSaleSubmittedDoc]);

  const [data, setData] = useState<OrderListResponse>();

  const [FullDeliveryDrawer, setFullDeliveryDrawer] = useState({
    isOpen: false
  });
  const [deliveryDrawer, setDeliveryDrawer] = useState({
    isOpen: false
  });
  const [supportFilesModal, setSupportFilesModal] = useState<{
    isOpen: boolean;
    data: FileAdditionalSupports[];
  }>({
    isOpen: false,
    data: []
  });

  const [imagesModal, setImagesModal] = useState<{
    isOpenFaceModal: boolean;
    isOpenDocumentModal: boolean;
  }>({
    isOpenFaceModal: false,
    isOpenDocumentModal: false
  });

  const [lotsMedication, setLotsMedication] = useState<{
    isOpen: boolean;
    data: { qtyUsed: number; id: number; medicineId: number }[];
  }>({
    isOpen: false,
    data: []
  });

  const [editingLot, setEditingLot] = useState<IEditLot | null>(null);

  const { data: orderDetail } = useGetOrderDetail(
    {
      orderId: resolvedOrderId,
      disId: resolvedDisId
    },
    Boolean(resolvedOrderId) || Boolean(resolvedDisId)
  );

  const contractId = orderDetail?.results?.orderInfo?.contractId;

  const { data: supportParamsList } = useGetSupportParamsList(
    {
      contractId: contractId ? String(contractId) : null,
      onlyActive: true
    },
    Boolean(contractId && !isFreeSale && !isFreeSaleWithOrder)
  );

  const medicalOrderTypeLabel = isFreeSale
    ? "Particular"
    : (medicalOrderTypeLabels[
        orderDetail?.results?.orderInfo?.medicalOrderType ?? storedMedicalOrderType ?? ""
      ] ?? "");

  const updateMedicineLot = useUpdateMedicineLot(async () => {
    queryClient.invalidateQueries({ queryKey: ["ordersDetail", resolvedOrderId, resolvedDisId] });
    setEditingLot(null);
  });

  useEffect(() => {
    if (orderDetail?.results) {
      setData(orderDetail.results);
    }
  }, [orderDetail?.results]);

  useEffect(() => {
    if (isFreeSale || isFreeSaleWithOrder) {
      return;
    }

    const requiredSupports = supportParamsList?.results?.at(0)?.supports ?? [];

    if (!requiredSupports.length) {
      return;
    }

    setData((prevState) => {
      if (!prevState) {
        return prevState;
      }

      const existingSupports = prevState.additionalSupports ?? [];
      const supportsById = new Map(existingSupports.map((support) => [support.rcsId, support]));

      const nextSupports: FileAdditionalSupports[] = requiredSupports.map((support) => {
        const existing = supportsById.get(Number(support.id));
        return (
          existing ?? {
            rcsId: Number(support.id),
            file: null,
            filename: ""
          }
        );
      });

      return {
        ...prevState,
        additionalSupports: nextSupports
      };
    });
  }, [supportParamsList, isFreeSale, isFreeSaleWithOrder]);

  type IOptionItem = (typeof documentList)[number] & { prefix?: string };

  const documentList = useMemo(() => {
    if (documentListRes?.results) {
      return [...formattedDataSelect(documentListRes.results, "description", "id", ["prefix"])];
    }
    return [];
  }, [documentListRes?.results]);

  const renderRow = (option: Medicine, idx: number) => {
    const isEditing = editingLot?.medicineId === option.id;

    return (
      <Table.Tr key={`${option.id}-${idx}`} data-testid={`delivery-medicine-row-${idx}`}>
        <Table.Td>{option.id}</Table.Td>
        <Table.Td>{option.name}</Table.Td>
        <Table.Td>
          <Box className="flex items-center justify-start gap-2">
            {isEditing ? (
              <>
                <TextInput
                  value={option.lot || ""}
                  onChange={(e) => handleLotChange(option.id!, e.target.value)}
                  placeholder="Escribe..."
                  size="xs"
                  data-testid={`delivery-medicine-lot-input-${idx}`}
                  maw={120}
                />
                <IconCheck
                  size={18}
                  className="pointer"
                  color="green"
                  data-testid={`delivery-medicine-lot-save-${idx}`}
                  onClick={() => handleSaveLot(option.id!)}
                />
                <IconX
                  size={18}
                  className="pointer"
                  color="red"
                  data-testid={`delivery-medicine-lot-cancel-${idx}`}
                  onClick={() => handleCancelLot(option.id!)}
                />
              </>
            ) : (
              <>
                <Text data-testid={`delivery-medicine-lot-text-${idx}`}>{option.lot || "-"}</Text>
                {showDetail && (
                  <IconEdit
                    size={18}
                    className="pointer primary-color"
                    data-testid={`delivery-medicine-lot-edit-${idx}`}
                    onClick={() => handleEditLot(option.id!, option.lot || "")}
                  />
                )}
              </>
            )}
          </Box>
        </Table.Td>
        <Table.Td ta={"center"}>{option.qtyStock ?? "-"}</Table.Td>
        <Table.Td ta={"center"}>{option.qtyOrder ?? "-"}</Table.Td>
        <Table.Td ta={"center"}>{option.qtyDispensed ?? "-"}</Table.Td>
        <Table.Td ta={"center"}>{option.qtyEstimated ?? "-"}</Table.Td>
        <Table.Td ta={"center"}>{option.qtyUsed}</Table.Td>
        <Table.Td ta={"center"}>
          {Number(option.qtyRemaining ?? 0) - Number(option.qtyUsed ?? 0)}
        </Table.Td>
        <Table.Td ta={"center"}>
          {!showDetail && (
            <Tooltip
              label={canDelete ? "Eliminar medicamento" : RBAC_LABELS.NO_PERMISSION}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Eliminar medicamento"
                disabled={!canDelete}
                className={!canDelete ? "noPermissionDisabled" : ""}
                data-testid={`delivery-medicine-delete-button-${idx}`}
                onClick={() => {
                  if (!canDelete) {
                    return;
                  }
                  handleRemoveMedicine(idx);
                }}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table mt={"sm"} mb="sm" className="biowel-table" data-testid="delivery-medicines-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"start"}>Código</Table.Th>
            <Table.Th>Medicamento</Table.Th>
            <Table.Th>Lote</Table.Th>
            <Table.Th ta={"center"}>Stock disp.</Table.Th>
            <Table.Th ta={"center"}>Ordenado</Table.Th>
            <Table.Th ta={"center"}>Entrega anterior</Table.Th>
            <Table.Th ta={"center"}>Estimado actual</Table.Th>
            <Table.Th ta={"center"}>Entrega real</Table.Th>
            <Table.Th ta={"center"}>Postfechado</Table.Th>
            <Table.Th ta={"center"}>Acción</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {data?.medicines?.length ? (
            data?.medicines?.map(renderRow)
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={10}>
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  const renderRowFreeSale = (option: Medicine, idx: number) => {
    return (
      <Table.Tr key={`${option.id}-${idx}`} data-testid={`delivery-freesale-row-${idx}`}>
        <Table.Td bg={"#00B4CC10"}>{option.id}</Table.Td>
        <Table.Td bg={"#00B4CC10"}>{option.name}</Table.Td>
        <Table.Td bg={"#00B4CC10"}>{option.lot}</Table.Td>
        <Table.Td bg={"#00B4CC10"} ta={"center"}>
          {option.qtyStock ?? "-"}
        </Table.Td>
        <Table.Td ta={"center"}>{option.qtyUsed ?? 0}</Table.Td>
        <Table.Td ta={"center"}>
          {!showDetail && (
            <Tooltip
              label={canDelete ? "Eliminar medicamento" : RBAC_LABELS.NO_PERMISSION}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Eliminar medicamento"
                disabled={!canDelete}
                className={!canDelete ? "noPermissionDisabled" : ""}
                data-testid={`delivery-freesale-delete-button-${idx}`}
                onClick={() => {
                  if (!canDelete) {
                    return;
                  }
                  handleRemoveMedicine(idx);
                }}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTableFreeSale = () => {
    return (
      <Table mt={"sm"} mb="sm" className="biowel-table" data-testid="delivery-freesale-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"start"}>Código</Table.Th>
            <Table.Th>Medicamento</Table.Th>
            <Table.Th>Lote</Table.Th>
            <Table.Th ta={"center"}>Stock disponible</Table.Th>
            <Table.Th ta={"center"}>Entrega real</Table.Th>
            <Table.Th ta={"center"}>Acción</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {data?.medicines?.length ? (
            data?.medicines?.map(renderRowFreeSale)
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={6}>
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  const onCloseLotsModal = () => {
    setLotsMedication((state) => ({ ...state, isOpen: false }));
  };

  const handleChangeCompanion = (value: string) => {
    setData((state) => ({
      ...state,
      patientSignature: value
    }));
  };

  const handlePictureFace = (file: string) => {
    setData((state) => ({
      ...state,
      patientFacePicture: file
    }));
  };
  const handlePictureDocument = (files: string[]) => {
    setData((state) => ({
      ...state,
      patientDocumentPictures: files
    }));
  };

  const handleChangeQtyLots = (lotsScanner: LotsScannerResponse) => {
    if (!lotsScanner.lots?.length) {
      return;
    }

    const isTopSource = Boolean(lotsScanner.isTop);
    const scannerLots = lotsScanner.lots;
    const validLots = scannerLots.filter((lot) => Number(lot.qtyUsed ?? 0) > 0);
    const touchedMedicineIds = new Set(scannerLots.map((lot) => Number(lot.medicineId)));
    const medicines = data?.medicines ?? orderDetail?.results?.medicines ?? [];

    const untouchedMedicines = medicines.filter(
      (medicine) => !touchedMedicineIds.has(Number(medicine.id))
    );

    const sourceMedicines = orderDetail?.results?.medicines ?? medicines;

    const medicinesByLot: Medicine[] = validLots.map((lot) => {
      const medicineId = Number(lot.medicineId);
      const templateMedicine =
        medicines.find((medicine) => medicine.id === medicineId) ??
        sourceMedicines.find((medicine) => medicine.id === medicineId);

      return {
        id: medicineId,
        articleId: lot.articleId,
        batchId: isTopSource ? undefined : Number(lot.id),
        name: templateMedicine?.name ?? lot.lotName,
        lot: lot.warehouseId,
        warehouseCode: lot.warehouseCode ?? templateMedicine?.warehouseCode,
        warehouseName: lot.warehouseName ?? templateMedicine?.warehouseName,
        isTop: isTopSource || undefined,
        qtyStock: lot.qtyAvailable,
        qtyUsed: Number(lot.qtyUsed ?? 0),
        qtyDispensed: Number(lot.qtyUsed ?? 0),
        qtyOrder: templateMedicine?.qtyOrder ?? 0,
        qtyRemaining: templateMedicine?.qtyRemaining,
        qtyEstimated: templateMedicine?.qtyEstimated,
        qtyDelivered: templateMedicine?.qtyDelivered,
        presentationId: templateMedicine?.presentationId,
        description: templateMedicine?.description ?? lotsScanner.description
      };
    });

    setData((prevState) => ({
      ...prevState,
      orderInfo: orderDetail?.results.orderInfo,
      medicines: [...untouchedMedicines, ...medicinesByLot]
    }));
  };

  const handleSupportFiles = (files: FileAdditionalSupports[]) => {
    setSupportFilesModal((state) => ({ ...state, data: files }));
    setData((state) => ({
      ...state,
      additionalSupports: files
    }));
  };

  const handleEditLot = (medicineId: number, currentLot: string) => {
    setEditingLot({ medicineId, lot: currentLot, originalLot: currentLot });
  };

  const handleLotChange = (medicineId: number, lot: string) => {
    if (alphanumericRegex.test(lot) && editingLot) {
      setEditingLot({ ...editingLot, lot });
      setData((prevState) => ({
        ...prevState,
        medicines: prevState?.medicines?.map((med) =>
          med.id === medicineId ? { ...med, lot } : med
        )
      }));
    }
  };

  const handleSaveLot = (medicineId: number) => {
    if (editingLot && editingLot.medicineId === medicineId && disId) {
      updateMedicineLot.mutate({
        dispensaryId: Number(disId),
        medicineId,
        lot: editingLot.lot
      });
    }
  };

  const handleCancelLot = (medicineId: number) => {
    if (editingLot && editingLot.medicineId === medicineId) {
      setData((prevState) => ({
        ...prevState,
        medicines: prevState?.medicines?.map((med) =>
          med.id === medicineId ? { ...med, lot: editingLot.originalLot } : med
        )
      }));
      setEditingLot(null);
    }
  };

  const handleRemoveMedicine = (medicineIndex: number) => {
    const medicineToRemove = data?.medicines?.[medicineIndex];

    setData((prevState) => ({
      ...prevState,
      medicines: prevState?.medicines?.filter((_, idx) => idx !== medicineIndex) ?? []
    }));

    if (medicineToRemove?.id === editingLot?.medicineId) {
      setEditingLot(null);
    }
  };

  const goBack = () => {
    navigate(-1);
    setDataDispense({ showDeliveryBy: false, orderId: null });
  };

  const onSubmitValidations = () => {
    if (!canCreate) {
      return;
    }
    if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.additionalSupports?.some((file) => !file.file) ?? false)
    ) {
      warningNotification("Debe adjuntar todos los soportes obligatorios", "Intenta de nuevo");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.disRequestByDocumentType === "" || data?.disRequestByDocumentType === undefined)
    ) {
      warningNotification("El tipo de documento es obligatorio", "Intenta de nuevo");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.receiveDocument === "" || data?.receiveDocument === undefined)
    ) {
      warningNotification("El documento de quien recibe es obligatorio", "Intenta de nuevo");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.receiveName === "" || data?.receiveName === undefined)
    ) {
      warningNotification("El nombre de quien recibe es obligatorio", "Intenta de nuevo");
      return;
    } else if (data?.authorizationNumber && !isAlphanumeric(data?.authorizationNumber)) {
      warningNotification("El número de autorización solo puede contener caracteres alfanuméricos");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.patientSignature === "" || data?.patientSignature === undefined)
    ) {
      warningNotification("La firma es obligatoria", "Intenta de nuevo");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      (data?.patientFacePicture === "" || data?.patientFacePicture === undefined)
    ) {
      warningNotification("La fotografía del rostro es obligatorio", "Intenta de nuevo");
      return;
    } else if (
      !isFreeSale &&
      !isFreeSaleWithOrder &&
      ((data?.patientDocumentPictures?.length ?? 0) !== 2 ||
        data?.patientDocumentPictures === undefined)
    ) {
      warningNotification(
        "Es necesario enviar la fotografía frontal y posterior del documento",
        "Intenta de nuevo"
      );
      return;
    } else if (!isFreeSaleWithOrder && !data?.medicines?.some((x) => x.qtyDispensed !== 0)) {
      warningNotification("La entrega real es obligatoria", "Intenta de nuevo");
      return;
    }
    setDeliveryDrawer((state) => ({
      ...state,
      isOpen: true
    }));
  };

  const clientInformationFreeSale = () => (
    <>
      <Card
        my={"md"}
        padding="lg"
        radius="md"
        __size="xl"
        className=""
        data-testid="delivery-client-info-freesale"
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            setFreeSaleSubmittedDoc(freeSaleDocNumber.trim());
          }}
        >
          <Grid align="flex-end">
            <Grid.Col span={4}>
              <Select
                label={
                  <Text fz="xs" fw="bold">
                    Tipo de documento
                    <Text component="span" c="red">
                      {" "}
                      *
                    </Text>
                  </Text>
                }
                data={documentList}
                searchable
                clearable
                value={freeSaleDocType}
                placeholder="Seleccionar..."
                data-testid="delivery-freesale-doctype"
                onChange={(val) => {
                  setFreeSaleDocType(val);
                  setFreeSaleSubmittedDoc("");
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label={
                  <Text fz="xs" fw="bold">
                    No. identificación
                    <Text component="span" c="red">
                      {" "}
                      *
                    </Text>
                  </Text>
                }
                placeholder="Escribe..."
                value={freeSaleDocNumber}
                data-testid="delivery-freesale-docnumber"
                onChange={({ target }) => {
                  setFreeSaleDocNumber(target.value);
                  setFreeSaleSubmittedDoc("");
                }}
              />
            </Grid.Col>
            <Grid.Col span={1} className="flex items-end">
              <Button
                type="submit"
                variant="subtle"
                loading={freeSaleSearching}
                disabled={!freeSaleDocNumber.trim() || !freeSaleDocType}
                data-testid="delivery-freesale-search-button"
                px="xs"
              >
                <IconSearch size={18} />
              </Button>
            </Grid.Col>
          </Grid>
          <Text
            fz="xs"
            mt="xs"
            c="secondary-color"
            className="pointer"
            data-testid="delivery-freesale-create-link"
            onClick={() => setCreatePatientModal(true)}
            style={{
              display:
                !freeSaleSubmittedDoc || freeSaleSearching || exactPatientResults.length > 0
                  ? "none"
                  : undefined
            }}
          >
            ¿No encontró el tercero?{" "}
            <Text component="span" fz="xs" c="secondary-color" fw="bold" td="underline">
              Haga clic aquí para crearlo
            </Text>
          </Text>
        </Box>
      </Card>
      {freeSaleSubmittedDoc && !freeSaleSearching && (
        <Box mt={"xs"} mb="lg">
          {exactPatientResults.length > 0 &&
            exactPatientResults.map((p) => (
              <Card
                key={p.id}
                padding="sm"
                radius="sm"
                className="pointer"
                data-testid={`delivery-freesale-patient-${p.id}`}
                onClick={() => {
                  setSelectedFreeSaleClient(p);
                }}
              >
                <Text fz="md" c="brand-color" fw={"bold"} mb="xs">
                  Información del cliente
                </Text>
                <Grid>
                  {documentListRes?.results?.find((dt) => dt.id === Number(freeSaleDocType))
                    ?.description === "NIT" ? (
                    <Grid.Col span={4.8}>
                      <Text fz="xs" fw={"inherit"}>
                        Razón social
                      </Text>
                      <Text fz="xs" c="secondary-color" fw={"bold"}>
                        {p.businessName}
                      </Text>
                    </Grid.Col>
                  ) : (
                    <>
                      <Grid.Col span={2.4}>
                        <Text fz="xs" fw={"inherit"}>
                          Nombres
                        </Text>
                        <Text fz="xs" c="secondary-color" fw={"bold"}>
                          {p.firstName}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={2.4}>
                        <Text fz="xs" fw={"inherit"}>
                          Apellidos
                        </Text>
                        <Text fz="xs" c="secondary-color" fw={"bold"}>
                          {p.lastName}
                        </Text>
                      </Grid.Col>
                    </>
                  )}
                  <Grid.Col span={2.4}>
                    <Text fz="xs" fw={"inherit"}>
                      Identificación
                    </Text>
                    <Text fz="xs" c="secondary-color" fw={"bold"}>
                      {p.documentType.name} {p.documentNumber}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={2.4}>
                    <Text fz="xs" fw={"inherit"}>
                      Email
                    </Text>
                    <Text fz="xs" c="secondary-color" fw={"bold"}>
                      {p.email}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={2.4}>
                    <Text fz="xs" fw={"inherit"}>
                      Ciudad
                    </Text>
                    <Text fz="xs" c="secondary-color" fw={"bold"}>
                      {p.city.name}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Card>
            ))}
        </Box>
      )}
    </>
  );
  const clientInformation = () => (
    <Card
      mb={"md"}
      padding="lg"
      radius="md"
      __size="xl"
      className=""
      data-testid="delivery-client-info"
    >
      <Text fz="md" mb="xs" c="brand-color" fw={"bold"}>
        Información del cliente
      </Text>
      <Grid>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Paciente
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.patient.name}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Identificación
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.patient?.documentType}
            {orderDetail?.results?.orderInfo?.patient?.document}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Edad
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {!orderDetail?.results?.orderInfo?.patient?.birthDate
              ? "-"
              : `${calculateAge(orderDetail?.results?.orderInfo?.patient?.birthDate ?? "")} años`}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Aseguradora
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.corporateClient?.name}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Contrato
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.contract}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Tipo de afiliado
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.patient?.affiliationType ?? "-"}
          </Text>
        </Grid.Col>
        <Grid.Col span={2.4}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            Población
          </Text>
          <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
            {orderDetail?.results?.orderInfo?.population ?? "-"}
          </Text>
        </Grid.Col>
      </Grid>
    </Card>
  );

  return (
    <Template
      title={
        showDetail
          ? `Detalle de la entrega${medicalOrderTypeLabel ? ` - ${medicalOrderTypeLabel}` : ""}`
          : `Entrega de Medicamentos${medicalOrderTypeLabel ? ` - ${medicalOrderTypeLabel}` : ""}`
      }
      goBackButton={() => {
        goBack();
      }}
    >
      <DeliveryFormToSend
        data={data}
        patient={isFreeSale ? selectedFreeSaleClient : dataPatient}
        onClose={() => {
          setDeliveryDrawer((state) => ({
            ...state,
            isOpen: false
          }));
        }}
        isOpen={deliveryDrawer.isOpen}
      />
      <CreatePatientModal
        isOpen={createPatientModal}
        onClose={() => setCreatePatientModal(false)}
        onCreated={() => {
          setFreeSaleSubmittedDoc(freeSaleDocNumber.trim());
          refetchFreeSale();
        }}
        initialDocumentTypeId={freeSaleDocType ?? undefined}
        initialDocumentNumber={freeSaleDocNumber}
      />

      <DeliverySupportModal
        data={supportFilesModal.data}
        supports={supportParamsList?.results?.at(0)?.supports}
        handleChange={handleSupportFiles}
        onClose={() => {
          setSupportFilesModal((state) => ({
            ...state,
            isOpen: false
          }));
        }}
        open={supportFilesModal.isOpen}
      />

      <FullDelivery
        dataPatient={orderDetail?.results?.orderInfo}
        onClose={() => {
          setFullDeliveryDrawer((state) => ({
            ...state,
            isOpen: false
          }));
        }}
        isOpen={FullDeliveryDrawer.isOpen}
      />

      <MedicationScanner
        orderId={Number(resolvedOrderId ?? 0)}
        patientId={isFreeSale ? undefined : dataPatient?.id}
        externalClientId={isFreeSale ? selectedFreeSaleClient?.id : undefined}
        isOpen={lotsMedication.isOpen}
        onClose={onCloseLotsModal}
        handleChange={handleChangeQtyLots}
        dispensaryId={Number(resolvedDisId)}
        isOwnInventory={deliveryBy === "ownInventory"}
        dataToValidate={{
          dataOrder: orderDetail?.results?.medicines,
          deliveryBy,
          fullDeliveryIsApproved
        }}
      />
      <DeliveryRegisterPicture
        setData={handlePictureFace}
        data={data?.patientFacePicture}
        isOpen={imagesModal.isOpenFaceModal}
        onClose={() => {
          setImagesModal((state) => ({
            ...state,
            isOpenFaceModal: false
          }));
        }}
      />
      <DeliveryRegisterMultiPicture
        setData={handlePictureDocument}
        data={data?.patientDocumentPictures ?? []}
        isOpen={imagesModal.isOpenDocumentModal}
        onClose={() => {
          setImagesModal((state) => ({
            ...state,
            isOpenDocumentModal: false
          }));
        }}
      />
      <Box>
        {isFreeSale ? clientInformationFreeSale() : clientInformation()}
        <Text fz="md" ml="xs" flex={1} c="brand-color" fw={"bold"}>
          {showDetail ? "Medicamentos entregados" : "Medicamentos a entregar"}{" "}
        </Text>

        {!isFreeSale && !detailWithoutOrder && (
          <div data-testid="delivery-gotodetail" className="flex justify-end items-center ">
            <Tooltip label="Ver fórmula" withArrow position="right">
              <IconEye
                data-testid="delivery-viewformula"
                onClick={() =>
                  navigate(routes.getDeliveryDetailOrder, {
                    state: { data: resolvedOrderId }
                  })
                }
                style={{ height: 30, width: 30 }}
                className="primary-color pointer"
              />
            </Tooltip>
          </div>
        )}
        {isFreeSale || isFreeSaleWithOrder ? renderTableFreeSale() : renderTable()}
        <Box className="flex justify-end gap-3" data-testid="delivery-action-buttons">
          {!showDetail && !isFreeSale && !isFreeSaleWithOrder && !isPendingCompleteDelivery && (
            <Box className="flex justify-end">
              <Button
                variant="outline"
                data-testid="delivery-requestfulldelivery"
                onClick={() =>
                  setFullDeliveryDrawer((state) => ({
                    ...state,
                    isOpen: true
                  }))
                }
              >
                Solicitar entrega completa
              </Button>
            </Box>
          )}
          {!(isFreeSale || !isFreeSaleWithOrder) && (
            <Box className="flex justify-end">
              <Button
                variant="filled"
                color="secondary-color"
                data-testid="delivery-loadorders"
                onClick={() => warningNotification("En desarrollo")}
              >
                Cargar ordenamientos
              </Button>
            </Box>
          )}

          {!showDetail && (
            <Box className="flex justify-end">
              <Button
                variant="filled"
                data-testid="delivery-scanmedications"
                onClick={() => {
                  if (isFreeSale && !selectedFreeSaleClient) {
                    warningNotification(
                      "Debe seleccionar un cliente externo antes de escanear medicamentos"
                    );
                    return;
                  }
                  setLotsMedication((state) => ({ ...state, isOpen: true }));
                }}
              >
                Escanear medicamentos
              </Button>
            </Box>
          )}
        </Box>
        {!isFreeSale && !detailWithoutOrder && (
          <>
            {!isFreeSale && !isFreeSaleWithOrder && (
              <Text fz="md" pt={"sm"} ml="xs" flex={1} c="brand-color" fw={"bold"}>
                Registro de entrega
              </Text>
            )}
            {!showDetail && !isFreeSale && !isFreeSaleWithOrder && (
              <Box className="flex justify-end " data-testid="delivery-supportsbutton-container">
                <Button
                  variant="light"
                  data-testid="delivery-opensupports"
                  onClick={() => {
                    setSupportFilesModal((state) => ({
                      ...state,
                      isOpen: true
                    }));
                  }}
                >
                  <IconUpload /> &nbsp; <span> Adjuntar soportes</span>
                </Button>
              </Box>
            )}
            <Box className="flex gap-3" my={"xs"} data-testid="delivery-registration-section">
              {!isFreeSale && !isFreeSaleWithOrder && (
                <Box w={"100%"} data-testid="delivery-facephoto-container">
                  <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
                    Fotografía del rostro
                  </Text>
                  <div className="cardCamera" data-testid="delivery-facephoto-card">
                    {data?.patientFacePicture ? (
                      <img
                        src={
                          data?.patientFacePicture.startsWith("http")
                            ? data?.patientFacePicture
                            : `data:application/pdf;base64,${data?.patientFacePicture}`
                        }
                        alt="pictureFace"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "2%"
                        }}
                      />
                    ) : (
                      <Text fz="md" ml="xs" fw={"inherit"}>
                        Tomar foto
                      </Text>
                    )}
                    {!disabledForm && (
                      <div className="cameraButton">
                        <IconCamera
                          style={{ width: 20, height: 20 }}
                          className="primary-color pointer"
                          data-testid="delivery-takefacephoto"
                          onClick={() => {
                            setImagesModal((state) => ({
                              ...state,
                              isOpenFaceModal: true
                            }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Box>
              )}
              {!isFreeSale && !isFreeSaleWithOrder && (
                <Box w={"100%"} data-testid="delivery-documentphoto-container">
                  <Text fz="xs" ml="xs" flex={1} c="secondary-color" fw={"bold"}>
                    Fotografía de la identificación
                  </Text>
                  <div className="cardCamera" data-testid="delivery-documentphoto-card">
                    <Box className={"flex containerDocumentPicture align-center text-center"}>
                      {data?.patientDocumentPictures &&
                      data?.patientDocumentPictures?.length > 0 ? (
                        <Carousel
                          classNames={classes}
                          withIndicators
                          withControls
                          height={"300px"}
                          slidesToScroll={1}
                          data-testid="delivery-documentphoto-carousel"
                          nextControlIcon={
                            <div
                              style={{
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                padding: "0.5rem"
                              }}
                            >
                              <IconArrowRight size={16} color="#00b4cd" />
                            </div>
                          }
                          previousControlIcon={
                            <div
                              style={{
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                padding: "0.5rem"
                              }}
                            >
                              <IconArrowLeft size={16} color="#00b4cd" />
                            </div>
                          }
                        >
                          {data?.patientDocumentPictures.map((image, ind) => (
                            <Carousel.Slide
                              key={ind}
                              id={`${ind}`}
                              data-testid={`delivery-documentphoto-slide-${ind}`}
                            >
                              <img
                                width={"100%"}
                                height={"260px"}
                                src={
                                  image.startsWith("http")
                                    ? image
                                    : `data:application/pdf;base64,${image}`
                                }
                                alt="pictureFace"
                              />
                              <Badge variant="light" px={"sm"} color="#00b4cc" size="lg">
                                {ind === 0 ? "Cara frontal" : "Cara posterior"} de documento
                              </Badge>{" "}
                            </Carousel.Slide>
                          ))}
                        </Carousel>
                      ) : (
                        <Text fz="md" ml="xs" fw={"inherit"}>
                          Tomar foto
                        </Text>
                      )}
                      {!disabledForm && (
                        <div className="cameraButton">
                          <IconCamera
                            style={{ width: 20, height: 20 }}
                            className="primary-color pointer"
                            data-testid="delivery-takedocumentphoto"
                            onClick={() => {
                              setImagesModal((state) => ({
                                ...state,
                                isOpenDocumentModal: true
                              }));
                            }}
                          />
                        </div>
                      )}
                    </Box>
                  </div>
                </Box>
              )}
              {!isFreeSale && !isFreeSaleWithOrder && (
                <Box w={"100%"} data-testid="delivery-receiverinfo-container">
                  <Grid>
                    <Grid.Col span={6}>
                      <Select
                        clearable
                        readOnly={disabledForm}
                        data-testid="delivery-documenttype"
                        label="Tipo de documento"
                        data={documentList}
                        searchable
                        value={String(
                          showDetail
                            ? data?.receiveDocumentType?.id
                            : data?.disRequestByDocumentType || ""
                        )}
                        onChange={(_, value: IOptionItem | null) => {
                          setData((state) => ({
                            ...state,
                            disRequestByDocumentType: value?.value,
                            documentPrefix: value?.prefix || ""
                          }));
                        }}
                        styles={{ label: { paddingTop: 0 } }}
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <TextInput
                        label="Documento de quien recibe"
                        placeholder="Escribir..."
                        type={data?.documentPrefix === "pa" ? "text" : "number"}
                        readOnly={disabledForm}
                        data-testid="delivery-receiverdocument"
                        value={data?.receiveDocument}
                        onChange={({ target }) => {
                          setData((state) => ({
                            ...state,
                            receiveDocument: target.value
                          }));
                        }}
                        styles={{ label: { paddingTop: 0 } }}
                      />
                    </Grid.Col>
                  </Grid>
                  <TextInput
                    label="Nombre de quien recibe"
                    readOnly={disabledForm}
                    placeholder="Escribir..."
                    data-testid="delivery-receivername"
                    value={data?.receiveName}
                    onChange={({ target }) => {
                      setData((state) => ({
                        ...state,
                        receiveName: target.value
                      }));
                    }}
                  />
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        label="No. Autorización"
                        placeholder="Escribir..."
                        type={"text"}
                        readOnly={disabledForm}
                        data-testid="delivery-authorizationnumber"
                        value={data?.authorizationNumber}
                        onChange={({ target }) => {
                          if (alphanumericRegex.test(target.value)) {
                            setData((state) => ({ ...state, authorizationNumber: target.value }));
                          }
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <TextInput
                        label="Fecha de autorización"
                        placeholder="Escribir..."
                        type="date"
                        readOnly={disabledForm}
                        data-testid="delivery-authorizationdate"
                        value={data?.authorizationDate}
                        onChange={({ target }) => {
                          setData((state) => ({
                            ...state,
                            authorizationDate: target.value
                          }));
                        }}
                      />
                    </Grid.Col>
                  </Grid>
                  <Box
                    className="flex flex-column"
                    mt="sm"
                    data-testid="delivery-signature-container"
                  >
                    <Text fz="xs" flex={1} c="secondary-color" fw={"inherit"}>
                      Firma
                    </Text>{" "}
                    <Box className="col-9 text-primary flex" w={"100%"} h={"100%"}>
                      <SignatureComponent
                        disabled={disabledForm}
                        handleChangeCompanion={handleChangeCompanion}
                        firm={data?.patientSignature}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      {!detailWithoutOrder && (
        <Box w={"100%"} data-testid="delivery-observations-container">
          <Grid>
            <Grid.Col span={12}>
              <Textarea
                label="Observaciones"
                readOnly={disabledForm}
                placeholder="Escribir..."
                data-testid="delivery-observations"
                value={data?.observations}
                onChange={({ currentTarget }) => {
                  setData((state) => ({
                    ...state,
                    observations: currentTarget.value
                  }));
                }}
              />
            </Grid.Col>
          </Grid>
        </Box>
      )}
      <Box py={"md"} className="flex justify-end gap-3" data-testid="delivery-final-actions">
        {!showDetail && (
          <Box className="flex justify-end">
            <Button variant="outline" data-testid="delivery-cancel" onClick={() => goBack()}>
              Cancelar
            </Button>
          </Box>
        )}
        {!showDetail && (
          <Tooltip
            label={canCreate ? "Establecer entrega" : RBAC_LABELS.NO_PERMISSION}
            withArrow
            position="top"
          >
            <Button
              variant="gradient"
              data-testid="delivery-submit"
              disabled={!canCreate}
              className={!canCreate ? "noPermissionDisabled" : ""}
              onClick={() => {
                if (!canCreate) {
                  return;
                }
                onSubmitValidations();
              }}
            >
              Establecer entrega
            </Button>
          </Tooltip>
        )}
      </Box>
    </Template>
  );
};

export default Delivery;
